import { Host, h } from "@stencil/core";
import { SyncWithRAF } from "../../common/sync-with-frames";
const AVAILABLE_SIZE_CUSTOM_VAR = "--ch-textblock-available-size";
const DISPLAYED_LINES_CUSTOM_VAR = "--ch-textblock-displayed-lines";
const LINE_HEIGHT_CUSTOM_VAR = "--ch-textblock-line-height";
/**
 * @status developer-preview
 *
 * @slot - The slot for the HTML content.
 */
export class ChTextBlock {
    constructor() {
        this.contentOverflows = false;
        this.accessibleRole = "p";
        this.autoGrow = false;
        this.caption = undefined;
        this.characterToMeasureLineHeight = "A";
        this.format = "text";
        this.showTooltipOnOverflow = false;
    }
    #currentAvailableHeight = -1;
    #availableHeight = -1;
    #contentHeight = -1;
    #lineHeight = -1;
    #totalLines = -1;
    #displayedLines = -1;
    #resizeObserver;
    #syncWithRAF; // Allocated at runtime to save resources
    // Refs
    #contentRef;
    #htmlContentRef;
    #lineMeasuringRef;
    contentOverflowsChanged(newValue) {
        this.overflowingContentChange.emit(newValue);
    }
    el;
    autoGrowChanged(newAutoGrow) {
        if (newAutoGrow) {
            this.#disconnectResizeObserver();
        }
    }
    formatChanged() {
        // Avoid memory leaks, since the containerRef variable will be destroyed
        this.#disconnectResizeObserver();
    }
    /**
     * Fired when the displayed lines overflows the control's content.
     * If `true`, the current content overflows the control.
     */
    overflowingContentChange;
    #getContentRef = () => this.format === "HTML" ? this.#htmlContentRef : this.#contentRef;
    #calculateDisplayedLines = () => {
        // - - - - - - - - - - - - - DOM read operations - - - - - - - - - - - - -
        if (this.#currentAvailableHeight === 0) {
            return;
        }
        const contentRef = this.#getContentRef();
        const currentContentHeight = contentRef.scrollHeight;
        const currentLineHeight = this.#lineMeasuringRef.getBoundingClientRect().height;
        // Nothing has change
        if (this.#availableHeight === this.#currentAvailableHeight &&
            this.#contentHeight === currentContentHeight &&
            this.#lineHeight === currentLineHeight) {
            return;
        }
        // Store new values
        this.#availableHeight = this.#currentAvailableHeight;
        this.#contentHeight = currentContentHeight;
        this.#lineHeight = currentLineHeight;
        this.contentOverflows = this.#currentAvailableHeight < currentContentHeight;
        // - - - - - - - - - - - - - DOM write operations - - - - - - - - - - - - -
        // Calculate how many lines can display the control without overflowing
        // the content
        const currentDisplayedLines = Math.max(Math.trunc(this.#currentAvailableHeight / currentLineHeight), 1);
        // Calculate the total lines displayed by the control, even if they
        // overflow the content
        const currentTotalLines = Math.max(Math.trunc(currentContentHeight / currentLineHeight), 1);
        // Update the line-height value even if the displayed lines didn't change
        contentRef.style.setProperty(LINE_HEIGHT_CUSTOM_VAR, `${currentLineHeight}px`);
        if (this.format === "HTML") {
            contentRef.style.setProperty(AVAILABLE_SIZE_CUSTOM_VAR, `${this.#availableHeight}px`);
        }
        // Nothing has change
        if (this.#displayedLines === currentDisplayedLines &&
            this.#totalLines === currentTotalLines) {
            return;
        }
        // Store new values
        this.#displayedLines = currentDisplayedLines;
        this.#totalLines = currentTotalLines;
        contentRef.style.setProperty(DISPLAYED_LINES_CUSTOM_VAR, `${currentDisplayedLines}`);
    };
    #setResizeObserverIfNecessary = () => {
        if (this.#resizeObserver) {
            return;
        }
        this.#syncWithRAF = new SyncWithRAF();
        this.#resizeObserver = new ResizeObserver(entries => {
            const textBlockEntry = entries.find(el => el.target === this.el);
            if (textBlockEntry) {
                this.#currentAvailableHeight =
                    textBlockEntry.contentBoxSize[0].blockSize;
            }
            this.#syncWithRAF.perform(this.#calculateDisplayedLines);
        });
        const contentRef = this.#getContentRef();
        this.#resizeObserver.observe(contentRef);
        this.#resizeObserver.observe(this.#lineMeasuringRef);
        this.#resizeObserver.observe(this.el, { box: "content-box" });
    };
    #disconnectResizeObserver = () => {
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = undefined;
        this.#syncWithRAF?.cancel();
        this.#syncWithRAF = undefined;
        // Reset values. Necessary when alternating between autoGrow values
        this.#currentAvailableHeight = -1;
        this.#availableHeight = -1;
        this.#contentHeight = -1;
        this.#lineHeight = -1;
        this.#totalLines = -1;
        this.#displayedLines = -1;
    };
    #autoGrowRender = () => (this.format === "text" ? this.caption : h("slot", null));
    #noAutoGrowRender = () => [
        h("div", { class: "line-measure", ref: el => (this.#lineMeasuringRef = el) }, this.characterToMeasureLineHeight),
        this.format === "text" ? (h("div", { class: "content", ref: el => (this.#contentRef = el) }, this.caption)) : (h("div", { class: "html-content", ref: el => (this.#htmlContentRef = el) }, h("slot", null)))
    ];
    componentDidRender() {
        if (!this.autoGrow) {
            this.#setResizeObserverIfNecessary();
            if (this.showTooltipOnOverflow && this.format === "text") {
                this.#syncWithRAF.perform(this.#calculateDisplayedLines);
            }
        }
    }
    disconnectedCallback() {
        this.#disconnectResizeObserver();
    }
    render() {
        return (h(Host, { key: 'bd9b692e56a7b5202dcd0db5ef454248c7e0c257', role: this.accessibleRole === "p" ? "paragraph" : "heading", "aria-level": this.accessibleRole === "p" ? null : this.accessibleRole[1], class: !this.autoGrow ? "ch-textblock--no-auto-grow" : undefined, title: this.showTooltipOnOverflow &&
                this.format === "text" &&
                !this.autoGrow &&
                this.contentOverflows
                ? this.caption
                : undefined }, this.autoGrow ? this.#autoGrowRender() : this.#noAutoGrowRender()));
    }
    static get is() { return "ch-textblock"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["textblock.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["textblock.css"]
        };
    }
    static get properties() {
        return {
            "accessibleRole": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "| \"h1\"\n    | \"h2\"\n    | \"h3\"\n    | \"h4\"\n    | \"h5\"\n    | \"h6\"\n    | \"p\"",
                    "resolved": "\"h1\" | \"h2\" | \"h3\" | \"h4\" | \"h5\" | \"h6\" | \"p\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the accessible role of the component, which improves the\nsemantic that the component models."
                },
                "attribute": "accessible-role",
                "reflect": false,
                "defaultValue": "\"p\""
            },
            "autoGrow": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property defines if the control size will grow automatically, to\nadjust to its content size.\n\nIf `false` the overflowing content will be displayed with an ellipsis.\nThis ellipsis takes into account multiple lines."
                },
                "attribute": "auto-grow",
                "reflect": false,
                "defaultValue": "false"
            },
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the content to be displayed when the control has `format = text`."
                },
                "attribute": "caption",
                "reflect": false
            },
            "characterToMeasureLineHeight": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the character used to measure the line height"
                },
                "attribute": "character-to-measure-line-height",
                "reflect": false,
                "defaultValue": "\"A\""
            },
            "format": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"text\" | \"HTML\"",
                    "resolved": "\"HTML\" | \"text\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "It specifies the format that will have the textblock control.\n\n - If `format` = `HTML`, the textblock control works as an HTML div and\n   the innerHTML will be taken from the default slot.\n\n - If `format` = `text`, the control works as a normal textblock control\n   and it is affected by most of the defined properties."
                },
                "attribute": "format",
                "reflect": false,
                "defaultValue": "\"text\""
            },
            "showTooltipOnOverflow": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to display a tooltip when the caption overflows the size of the\ncontainer.\n\nOnly works if `format = text` and `autoGrow = false`."
                },
                "attribute": "show-tooltip-on-overflow",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "contentOverflows": {}
        };
    }
    static get events() {
        return [{
                "method": "overflowingContentChange",
                "name": "overflowingContentChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the displayed lines overflows the control's content.\nIf `true`, the current content overflows the control."
                },
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "contentOverflows",
                "methodName": "contentOverflowsChanged"
            }, {
                "propName": "autoGrow",
                "methodName": "autoGrowChanged"
            }, {
                "propName": "format",
                "methodName": "formatChanged"
            }];
    }
}
